import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formApi, formsApi } from 'app/services/api';
import _ from 'lodash';

import { FeatureFlag } from 'constants/featureFlags';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getServerErrorMessage } from 'utils/error';

import { getFormBundleForLoanByLoanIdAction } from 'store/forms/actions';
import { selectFormBundles } from 'store/forms/selectors';
import { selectFeatureFlagByKey } from 'store/global/selectors';

import {
  PREVIEW_PROGRESS_COMPLETE,
  PREVIEW_PROGRESS_TICK_MS,
  PreviewStatus,
} from '../../previewGeneration.constants';
import { computePreviewProgress, resolvePreviewTarget } from '../../previewGeneration.utils';

import { isRenderablePdf } from './utils';

const usePreviewDocument = ({ applicationId, loanId, versionHash }) => {
  const dispatch = useDispatch();
  const bundles = useSelector(selectFormBundles);
  const isFormsConsolidationEnabled = useSelector(
    selectFeatureFlagByKey(FeatureFlag.FormsConsolidationEnabled),
  );

  const previewApi = isFormsConsolidationEnabled ? formsApi : formApi;

  const target = useMemo(() => resolvePreviewTarget(Object.values(bundles ?? {})), [bundles]);

  const [status, setStatus] = useState(PreviewStatus.IDLE);
  const [progress, setProgress] = useState(0);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const objectUrlRef = useRef(null);
  const documentBlobRef = useRef(null);
  const intervalRef = useRef(null);
  const startedAtRef = useRef(0);
  const requestIdRef = useRef(0);

  const stopTicker = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const revokeUrl = useCallback(() => {
    if (objectUrlRef.current) {
      window.URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const generate = useCallback(() => {
    if (!applicationId || !target?.formId) {
      setStatus(PreviewStatus.UNAVAILABLE);

      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    revokeUrl();
    setDocumentUrl(null);
    setErrorMessage(null);
    setStatus(PreviewStatus.GENERATING);
    setProgress(0);
    startedAtRef.current = Date.now();

    stopTicker();
    intervalRef.current = setInterval(() => {
      setProgress(
        computePreviewProgress({
          status: PreviewStatus.GENERATING,
          elapsedMs: Date.now() - startedAtRef.current,
        }),
      );
    }, PREVIEW_PROGRESS_TICK_MS);

    previewApi
      .getGeneratedFormByApplicationId({ applicationId, formId: target.formId })
      .then(({ data }) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        stopTicker();

        if (!isRenderablePdf(data)) {
          setStatus(PreviewStatus.ERROR);
          setProgress(0);

          return;
        }

        const url = window.URL.createObjectURL(data);
        objectUrlRef.current = url;
        documentBlobRef.current = data;
        setDocumentUrl(url);
        setProgress(PREVIEW_PROGRESS_COMPLETE);
        setStatus(PreviewStatus.READY);
      })
      .catch(async err => {
        const serverMessage = await getServerErrorMessage(err);

        if (requestId !== requestIdRef.current) {
          return;
        }

        stopTicker();
        setErrorMessage(serverMessage);
        setStatus(PreviewStatus.ERROR);
        setProgress(0);
      });
  }, [previewApi, applicationId, target, revokeUrl, stopTicker]);

  useEffectSafeStrictMode(() => {
    if (loanId) {
      dispatch(getFormBundleForLoanByLoanIdAction({ loanId, silent: true })).catch(_.noop);
    }
  }, [dispatch, loanId]);

  useEffectSafeStrictMode(() => {
    generate();
  }, [generate, versionHash]);

  useEffect(
    () => () => {
      stopTicker();
      revokeUrl();
    },
    [stopTicker, revokeUrl],
  );

  const getDocumentCall = useCallback(() => ({ data: documentBlobRef.current }), []);

  return {
    status,
    progress,
    documentUrl,
    errorMessage,
    getDocumentCall,
    formName: target?.formName ?? null,
    formBundleId: target?.formBundleId ?? null,
    formId: target?.formId ?? null,
    retry: generate,
  };
};

export default usePreviewDocument;
